import { useEffect, useState } from "react"
import styled from "styled-components"
import AdminService from "../../../services/admin.service"
import { Parque, ParqueTarifa } from "../../../types/common.types"
import { mobile } from "../../../styles/responsive"
import buildParquesPdf from "../../../utils/parquesPdf"

// Parques y sus tarifas de entrada. Cada parque se carga una vez; los tours solo
// eligen a qué parques entran (en "Editar info" del tour). Así dos tours que van al
// mismo parque cobran siempre la misma entrada, y el bot cotiza con estas tarifas.

const CATEGORIAS = ['general', 'joven', 'niño', 'infante', 'adulto mayor', 'estudiante', 'necesidades especiales', 'indígena', 'atacameño']

interface Borrador { id?: string; nombre: string; activo: boolean; tarifas: ParqueTarifa[] }

const tarifaVacia = (): ParqueTarifa => ({ categoria: 'general', nacionalidad: null, edad_min: '', edad_max: '', requiere_acreditacion: false, precio_clp: '' })

const pesos = (n: number | string) => '$' + Number(n).toLocaleString('es-CL')

function edades(t: ParqueTarifa) {
    const min = t.edad_min === null || t.edad_min === '' ? null : t.edad_min
    const max = t.edad_max === null || t.edad_max === '' ? null : t.edad_max
    if (min === null && max === null) return '—'
    if (max === null) return `${min}+ años`
    return `${min ?? 0}–${max} años`
}

function errorDe(error: any) {
    const data = error?.response?.data
    return typeof data === 'string' && data ? data : 'No se pudo guardar'
}

export default function Parques() {
    const [parques, setParques] = useState<Parque[]>([])
    const [loading, setLoading] = useState(false)
    const [borrador, setBorrador] = useState<Borrador | null>(null)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')

    const load = async () => {
        setLoading(true)
        try { const { data } = await AdminService.listParques(); setParques(Array.isArray(data) ? data : []) }
        catch { setParques([]) } finally { setLoading(false) }
    }
    useEffect(() => { load() }, [])

    const nuevo = () => { setError(''); setBorrador({ nombre: '', activo: true, tarifas: [tarifaVacia()] }) }
    const editar = (p: Parque) => {
        setError('')
        setBorrador({ id: p.id, nombre: p.nombre, activo: p.activo, tarifas: p.tarifas.map(t => ({ ...t, edad_min: t.edad_min ?? '', edad_max: t.edad_max ?? '' })) })
    }
    const cancelar = () => { setBorrador(null); setError('') }

    const cambiarTarifa = (idx: number, campo: keyof ParqueTarifa, valor: any) => {
        if (!borrador) return
        setBorrador({ ...borrador, tarifas: borrador.tarifas.map((t, i) => i === idx ? { ...t, [campo]: valor } : t) })
    }
    const agregarTarifa = () => borrador && setBorrador({ ...borrador, tarifas: [...borrador.tarifas, tarifaVacia()] })
    const quitarTarifa = (idx: number) => borrador && setBorrador({ ...borrador, tarifas: borrador.tarifas.filter((_, i) => i !== idx) })

    const guardar = async () => {
        if (!borrador) return
        setSaving(true); setError('')
        try {
            const datos = { nombre: borrador.nombre.trim(), activo: borrador.activo, tarifas: borrador.tarifas }
            if (borrador.id) await AdminService.editParque({ id: borrador.id, ...datos })
            else await AdminService.registerParque(datos)
            setBorrador(null); await load()
        } catch (e) { setError(errorDe(e)) } finally { setSaving(false) }
    }

    const borrar = async (p: Parque) => {
        if (!window.confirm(`¿Eliminar el parque "${p.nombre}" y sus tarifas?`)) return
        try { await AdminService.deleteParque(p.id); await load() }
        catch (e) { window.alert(errorDe(e)) }
    }

    // PDF con las tarifas de todos los parques activos, para mandarlo cuando cambia alguna.
    const descargarPdf = () => {
        const hoy = new Date()
        const doc = buildParquesPdf(parques, { fecha: hoy, logo: '/assets/logo.png' })
        const fecha = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`
        doc.save(`entradas-parques-${fecha}.pdf`)
    }

    const editor = borrador && (
        <Editor>
            <h3>{borrador.id ? `Editar ${borrador.nombre}` : 'Nuevo parque'}</h3>
            <div className="row">
                <input placeholder="Nombre del parque (ej. Valle de la Luna)" value={borrador.nombre} onChange={e => setBorrador({ ...borrador, nombre: e.target.value })} />
                <label className="check">
                    <input type="checkbox" checked={borrador.activo} onChange={e => setBorrador({ ...borrador, activo: e.target.checked })} />
                    Activo (el bot cobra su entrada)
                </label>
            </div>
            <div className="tarifas">
                <div className="tarifa head">
                    <span>Categoría</span><span>Nacionalidad</span><span>Edad mín.</span><span>Edad máx.</span><span>Precio CLP</span><span>Acreditación</span><span></span>
                </div>
                {borrador.tarifas.map((t, idx) => (
                    <div className="tarifa" key={idx}>
                        <select value={t.categoria} onChange={e => cambiarTarifa(idx, 'categoria', e.target.value)}>
                            {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <select value={t.nacionalidad || 'ambos'} onChange={e => cambiarTarifa(idx, 'nacionalidad', e.target.value === 'ambos' ? null : e.target.value)}>
                            <option value="ambos">Ambos</option>
                            <option value="chileno">Chileno</option>
                            <option value="extranjero">Extranjero</option>
                        </select>
                        <input type="number" min={0} placeholder="—" value={t.edad_min ?? ''} onChange={e => cambiarTarifa(idx, 'edad_min', e.target.value)} />
                        <input type="number" min={0} placeholder="—" value={t.edad_max ?? ''} onChange={e => cambiarTarifa(idx, 'edad_max', e.target.value)} />
                        <input type="number" min={0} placeholder="0 = gratis" value={t.precio_clp} onChange={e => cambiarTarifa(idx, 'precio_clp', e.target.value)} />
                        <label className="check">
                            <input type="checkbox" checked={!!t.requiere_acreditacion} onChange={e => cambiarTarifa(idx, 'requiere_acreditacion', e.target.checked)} />
                            Requiere
                        </label>
                        <button className="quitar" type="button" onClick={() => quitarTarifa(idx)} title="Quitar tarifa">✕</button>
                    </div>
                ))}
                <button className="agregar" type="button" onClick={agregarTarifa}>+ Agregar tarifa</button>
            </div>
            {error && <p className="error">{error}</p>}
            <div className="row end">
                <button className="cancel" disabled={saving} onClick={cancelar}>Cancelar</button>
                <button className="save" disabled={saving || !borrador.nombre.trim()} onClick={guardar}>{saving ? 'Guardando…' : 'Guardar'}</button>
            </div>
        </Editor>
    )

    return (
        <Wrap>
            <Head>
                <div>
                    <p>Tarifas de entrada que Molantur gestiona y cobra. Cada tour elige a qué parques entra en "Editar info" del tour.</p>
                </div>
                {!borrador && (
                    <div className="botones">
                        <button className="pdf" disabled={!parques.some(p => p.activo && p.tarifas.length)} onClick={descargarPdf}>Descargar PDF</button>
                        <button className="nuevo" onClick={nuevo}>+ Nuevo parque</button>
                    </div>
                )}
            </Head>
            {borrador && !borrador.id && editor}
            {loading && <Empty>Cargando…</Empty>}
            {!loading && parques.length === 0 && !borrador && <Empty>Todavía no hay parques</Empty>}
            {parques.map(p => borrador?.id === p.id ? <div key={p.id}>{editor}</div> : (
                <Card key={p.id} className={p.activo ? '' : 'off'}>
                    <div className="top">
                        <div>
                            <strong>{p.nombre}</strong>{!p.activo && <span className="tag">Inactivo</span>}
                            <span className="meta">
                                {p.tours.length ? `${p.tours.length} tour(s): ${p.tours.map(t => t.title).join(' · ')}` : 'Ningún tour lo usa'}
                            </span>
                        </div>
                        <div className="actions">
                            <button className="edit" disabled={!!borrador} onClick={() => editar(p)}>Editar</button>
                            <button className="delete" disabled={!!borrador} onClick={() => borrar(p)}>Eliminar</button>
                        </div>
                    </div>
                    {p.tarifas.length ? (
                        <table>
                            <thead><tr><th>Categoría</th><th>Nacionalidad</th><th>Edad</th><th>Precio</th></tr></thead>
                            <tbody>
                                {p.tarifas.map((t, i) => (
                                    <tr key={t.id || i}>
                                        <td>{t.categoria}{t.requiere_acreditacion && <span className="acr"> · req. acreditación</span>}</td>
                                        <td>{t.nacionalidad ? t.nacionalidad : 'Ambos'}</td>
                                        <td>{edades(t)}</td>
                                        <td className="precio">{Number(t.precio_clp) === 0 ? 'Gratis' : pesos(t.precio_clp)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : <span className="meta">Sin tarifas: la entrada no se cobra.</span>}
                </Card>
            ))}
        </Wrap>
    )
}

const Wrap = styled.div`display:flex;flex-direction:column;gap:12px;padding-bottom:40px;`
const Head = styled.div`
display:flex;justify-content:space-between;align-items:flex-start;gap:12px;
p{margin:0;font-size:13px;color:#8B7355;}
.botones{display:flex;gap:8px;flex-shrink:0;}
button{border-radius:8px;padding:9px 16px;cursor:pointer;font-size:13px;white-space:nowrap;}
button.nuevo{border:none;background:#C4622D;color:#fff;}
button.pdf{border:1px solid #C4622D;background:#fff;color:#C4622D;}
button:disabled{opacity:.5;cursor:default;}
`
const Card = styled.div`
border:1px solid #e3ddd2;border-radius:10px;padding:12px 14px;background:#fff;
&.off{opacity:.6;}
.top{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;margin-bottom:8px;}
strong{font-size:15px;color:#2D2318;}
.tag{margin-left:8px;font-size:11px;background:#f2ebe0;color:#8B7355;border-radius:6px;padding:2px 6px;}
.meta{display:block;font-size:12px;color:#A09070;margin-top:2px;}
.actions{display:flex;gap:6px;flex-shrink:0;}
.actions button{border-radius:10px;padding:6px 14px;cursor:pointer;font-size:12px;}
.actions button.edit{background:transparent;color:#8B7355;border:1px solid #e3ddd2;}
.actions button.delete{background:transparent;color:#c0392b;border:1px solid #f0d4cf;}
.actions button:disabled{opacity:.5;cursor:default;}
table{width:100%;border-collapse:collapse;font-size:13px;}
th{text-align:left;font-weight:600;color:#8B7355;font-size:12px;border-bottom:1px solid #eee;padding:4px 6px;}
td{padding:4px 6px;border-bottom:1px solid #f5f1ea;color:#3c352b;}
td.precio{text-align:right;white-space:nowrap;}
.acr{color:#A09070;font-size:11px;}
${mobile({ padding: '10px' })}
`
const Editor = styled.div`
border:1px solid #C4622D;border-radius:10px;padding:14px;background:#faf7f1;display:flex;flex-direction:column;gap:10px;
h3{margin:0;font-size:15px;color:#2D2318;}
input,select{border:1px solid #d9cfbf;border-radius:8px;padding:7px;font-family:inherit;font-size:13px;box-sizing:border-box;background:#fff;min-width:0;}
.row{display:flex;gap:10px;align-items:center;flex-wrap:wrap;}
.row > input{flex:1;min-width:200px;}
.row.end{justify-content:flex-end;}
label.check{display:flex;align-items:center;gap:6px;font-size:13px;color:#3c352b;white-space:nowrap;}
label.check input{width:auto;}
.tarifas{display:flex;flex-direction:column;gap:6px;}
.tarifa{display:grid;grid-template-columns:1.6fr 1.2fr .8fr .8fr 1fr 1fr 32px;gap:6px;align-items:center;}
.tarifa.head span{font-size:11px;color:#8B7355;font-weight:600;}
@media (max-width: 700px){
    .tarifa{grid-template-columns:1fr 1fr;}
    .tarifa.head{display:none;}
}
button{border:none;border-radius:8px;padding:8px 14px;cursor:pointer;font-size:13px;}
button.quitar{background:transparent;color:#c0392b;padding:4px;}
button.agregar{align-self:flex-start;background:#f2ebe0;color:#8B7355;}
button.save{background:#C4622D;color:#fff;}
button.cancel{background:#f2ebe0;color:#8B7355;}
button:disabled{opacity:.5;cursor:default;}
.error{margin:0;color:#c0392b;font-size:13px;}
`
const Empty = styled.div`color:#A09070;font-size:13px;text-align:center;padding:24px;`
